import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import { MillosMark, MillosWordmark } from '../marketing/components/icons';
import { homepageStyles } from '../theme_landing_page_2';

const Login: React.FC = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Sign in | MillOS';
    return () => { document.title = previousTitle; };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { palette } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState<boolean>(!!(location.state as any)?.sessionExpired);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);

  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSessionExpired(false);
    setLoading(true);

    try {
      await login(email, password);
      // Login successful - user will be redirected by the auth context
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setForgotPasswordError(null);
    setForgotPasswordSuccess(false);
    setForgotPasswordLoading(true);

    try {
      const response = await fetch(`${config.apiUrl}/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordSuccess(true);
        setForgotPasswordEmail('');
      } else {
        setForgotPasswordError(data.error || 'Failed to send reset email');
      }
    } catch (err) {
      setForgotPasswordError('An error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordEmail('');
    setForgotPasswordError(null);
    setForgotPasswordSuccess(false);
  };

  const fieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: palette.tokens.canvas,
      '& fieldset': { borderColor: palette.tokens.hairline },
      '&:hover fieldset': { borderColor: palette.tokens.mutedSoft },
      '&.Mui-focused fieldset': { borderColor: palette.tokens.primary, borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': {
      py: '14px',
      fontSize: '15px',
    },
    // Browsers (Chrome especially) force their own blue/yellow tint on autofilled inputs,
    // overriding the canvas background above — this is the standard trick to override that back.
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${palette.tokens.canvas} inset`,
      WebkitTextFillColor: palette.tokens.ink,
      caretColor: palette.tokens.ink,
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        minHeight: '100svh',
        boxSizing: 'border-box',
        px: 2,
        py: { xs: 4, sm: 6 },
        bgcolor: palette.tokens.canvas,
        backgroundImage: `radial-gradient(ellipse at 15% 15%, ${alpha(palette.tokens.primary, 0.08)}, transparent 55%), radial-gradient(ellipse at 85% 85%, ${alpha(palette.tokens.accentTeal, 0.08)}, transparent 55%)`,
      }}
    >
      <Link component={RouterLink} to="/" aria-label="MillOS home" underline="none" sx={{ ...homepageStyles, display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 3, borderRadius: 2, '&:focus-visible': { outline: `2px solid ${palette.tokens.primary}`, outlineOffset: 6 } }}>
        <MillosMark size={48} />
        <MillosWordmark fontSize="1.8rem" />
      </Link>
      <Card sx={{ maxWidth: 440, width: '100%', borderRadius: '24px', border: `1px solid ${palette.tokens.hairline}`, backgroundColor: palette.tokens.canvas, boxShadow: `0 16px 48px ${alpha(palette.tokens.ink, 0.06)}` }}>
        <CardContent sx={{ p: { xs: 3, sm: 4.5 }, '&:last-child': { pb: { xs: 3, sm: 4.5 } } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="displayMd" component="h1" sx={{ color: palette.tokens.ink, mb: 1 }}>
              Welcome back.
            </Typography>
            <Typography variant="bodySm" component="p" sx={{ color: palette.tokens.muted }}>
              Sign in to your account
            </Typography>
          </Box>

          {sessionExpired && !error && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSessionExpired(false)}>
              Your session has expired. Please log in again.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              sx={fieldStyles}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              sx={fieldStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      aria-pressed={showPassword}
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff sx={{ color: palette.tokens.mutedSoft }} /> : <Visibility sx={{ color: palette.tokens.mutedSoft }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setForgotPasswordOpen(true)}
                sx={{ textDecoration: 'none', color: palette.tokens.primary }}
                type="button"
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3, mb: 1.5, py: 1.5, borderRadius: '12px', boxShadow: 'none', textTransform: 'none',
                fontSize: '15px', fontWeight: 700,
                bgcolor: palette.tokens.primary,
                '&:hover': { bgcolor: palette.tokens.primaryActive, boxShadow: 'none' },
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} sx={{ color: palette.tokens.onPrimary }} /> : 'Sign In'}
            </Button>
          </form>

          <Box sx={{ textAlign: 'center', mt: 3, pt: 2.5, borderTop: `1px solid ${palette.tokens.hairline}` }}>
            <Typography variant="bodySm" sx={{ color: palette.tokens.muted }}>
              Don't have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/register')}
                sx={{ textDecoration: 'none', color: palette.tokens.primary, fontWeight: 600 }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Link component={RouterLink} to="/" variant="bodySm" sx={{ mt: 3, color: palette.tokens.muted, textDecorationColor: palette.tokens.hairline, textUnderlineOffset: '4px' }}>
        Back to MillOS
      </Link>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: palette.tokens.ink }}>Reset Password</DialogTitle>
        <DialogContent>
          {forgotPasswordSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              If an account with that email exists, a password reset link has been sent to your email.
            </Alert>
          ) : (
            <>
              <Typography variant="bodySm" sx={{ mb: 2, mt: 1, color: palette.tokens.muted }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                margin="normal"
                required
                disabled={forgotPasswordLoading}
                sx={fieldStyles}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseForgotPassword} disabled={forgotPasswordLoading} sx={{ color: palette.tokens.muted, textTransform: 'none' }}>
            {forgotPasswordSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!forgotPasswordSuccess && (
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              disabled={forgotPasswordLoading || !forgotPasswordEmail}
              sx={{ borderRadius: '8px', boxShadow: 'none', textTransform: 'none', bgcolor: palette.tokens.primary, '&:hover': { bgcolor: palette.tokens.primaryActive, boxShadow: 'none' } }}
            >
              {forgotPasswordLoading ? <CircularProgress size={22} sx={{ color: palette.tokens.onPrimary }} /> : 'Send Reset Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
